/**
 * ── Hall Recommendation Engine ─────────────────────────────────────────────────
 *
 * Price-optimized hall selection for banquet bookings.
 *
 * Uses HALL_OPTIONS from constants as the source of truth for valid hall
 * combinations. Each option already encodes adjacency rules (only valid
 * adjacent combos exist in the list) and business rules (no Nova + Eclipse).
 *
 * Pricing comes from the backend availability API: { hallKey → { flatFee, pricePerPerson } }
 */

import { HALL_OPTIONS } from "../Pricing/constants";

// ── Types ────────────────────────────────────────────────────────────────────

/** Pricing record returned by the availability API for a given hallKey + dayType + timeSlot */
export interface HallPricing {
  flatFee: number;
  pricePerPerson: number;
}

/** A scored recommendation ready for display */
export interface HallRecommendation {
  hallKey: string;
  capacity: number;
  totalPrice: number;
  pricePerGuest: number;
  flatFee: number;
  pricePerPerson: number;
  wastedCapacity: number;
  tag: "best_value" | "cheapest" | "premium" | null;
}

// ── Shared pricing calculator ───────────────────────────────────────────────

export interface HallPriceResult {
  flatFee: number;
  perPersonPrice: number;
  perPersonTotal: number;
  total: number;
  guestCount: number;
  capacity: number;
  valid: boolean;            // false when guestCount > capacity
  pricePerGuest: number;     // total / guestCount (0 if guestCount is 0)
}

/**
 * Single source of truth for hall pricing.
 * Use this everywhere a hall total needs to be computed or displayed.
 *
 * When guests >= capacity: total = perPersonPrice × guestCount  (flat fee waived)
 * When guests <  capacity: total = flatFee + (perPersonPrice × guestCount)
 *
 * Returns `valid: false` when guestCount exceeds capacity.
 */
export function calculateHallPrice(params: {
  flatFee: number;
  perPersonPrice: number;
  guestCount: number;
  capacity: number;
}): HallPriceResult {
  const { flatFee, perPersonPrice, guestCount, capacity } = params;
  const valid = guestCount <= capacity;
  const perPersonTotal = perPersonPrice * guestCount;
  // Flat fee only applies when guests are below hall capacity
  const effectiveFlatFee = guestCount >= capacity ? 0 : flatFee;
  const total = effectiveFlatFee + perPersonTotal;
  return {
    flatFee: effectiveFlatFee,
    perPersonPrice,
    perPersonTotal: Math.round(perPersonTotal * 100) / 100,
    total: Math.round(total * 100) / 100,
    guestCount,
    capacity,
    valid,
    pricePerGuest: guestCount > 0 ? Math.round((total / guestCount) * 100) / 100 : 0,
  };
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const BASE_HALLS = ["Nova", "Titan", "Lunar", "Eclipse"] as const;
const hallsInKey = (key: string): string[] =>
  (BASE_HALLS as readonly string[]).filter((h) => key.includes(h));

/**
 * Checks whether a hall option is fully available for the requested time slot.
 * A combo like "Nova + Titan" requires BOTH Nova and Titan to be free.
 */
function isOptionAvailable(
  hallKey: string,
  timeSlot: string,
  occupied: Record<string, Record<string, any>>,
): boolean {
  const baseHalls = hallsInKey(hallKey);
  for (const h of baseHalls) {
    const info = occupied[h];
    if (!info) continue;
    if (timeSlot === "FULL_DAY" && (info["AM"] || info["PM"] || info["FULL_DAY"])) return false;
    if (info[timeSlot] || info["FULL_DAY"]) return false;
  }
  return true;
}

// ── Core Algorithm ───────────────────────────────────────────────────────────

/**
 * Returns all valid hall options that can fit the guest count,
 * sorted and tagged for display.
 *
 * @param guestCount   - Expected number of guests
 * @param timeSlot     - Selected time slot (AM | PM | FULL_DAY)
 * @param occupied     - Occupied map from availability API
 * @param pricingMap   - hallKey → HallPricing for the selected dayType + timeSlot
 */
export function getHallRecommendations(
  guestCount: number,
  timeSlot: string,
  occupied: Record<string, Record<string, any>>,
  pricingMap: Record<string, HallPricing>,
): HallRecommendation[] {
  if (guestCount <= 0) return [];

  // 1. Filter to available options that fit the guest count AND have pricing
  const candidates = HALL_OPTIONS.filter((opt) => {
    if (opt.capacity < guestCount) return false;
    if (!isOptionAvailable(opt.key, timeSlot, occupied)) return false;
    if (!pricingMap[opt.key]) return false;
    return true;
  });

  if (candidates.length === 0) return [];

  // 2. Score each candidate using shared pricing calculator
  const scored: HallRecommendation[] = candidates.map((opt) => {
    const pricing = pricingMap[opt.key];
    const calc = calculateHallPrice({
      flatFee: pricing.flatFee,
      perPersonPrice: pricing.pricePerPerson,
      guestCount,
      capacity: opt.capacity,
    });
    return {
      hallKey: opt.key,
      capacity: opt.capacity,
      totalPrice: calc.total,
      pricePerGuest: calc.pricePerGuest,
      flatFee: calc.flatFee,
      pricePerPerson: pricing.pricePerPerson,
      wastedCapacity: opt.capacity - guestCount,
      tag: null,
    };
  });

  // 3. Sort by total price ascending (primary), then wasted capacity ascending (secondary)
  scored.sort((a, b) => a.totalPrice - b.totalPrice || a.wastedCapacity - b.wastedCapacity);

  // 4. Tag cheapest and best value
  //    Cheapest  = lowest total price (first in sorted list)
  //    Best value = lowest price per guest among options with capacity closest to guest count
  const cheapest = scored[0];
  cheapest.tag = "cheapest";

  // Best value: sort a copy by wastedCapacity ASC, then pricePerGuest ASC
  const byValue = [...scored].sort(
    (a, b) => a.wastedCapacity - b.wastedCapacity || a.pricePerGuest - b.pricePerGuest,
  );
  const bestValue = byValue[0];

  // Only mark best_value if it's different from cheapest
  if (bestValue.hallKey !== cheapest.hallKey) {
    const match = scored.find((r) => r.hallKey === bestValue.hallKey);
    if (match) match.tag = "best_value";
  } else {
    // Same option is both cheapest and best value — mark as best_value (more prominent)
    cheapest.tag = "best_value";
  }

  // Tag "premium" — highest capacity option (if different from best_value and cheapest)
  const premium = [...scored].sort((a, b) => b.capacity - a.capacity)[0];
  if (premium && premium.tag === null) {
    premium.tag = "premium";
  }

  return scored;
}

/**
 * Returns the single best hall combination for a given mode.
 * This is the "auto-select" function used when guest count changes.
 */
export function getBestHallCombination(
  guestCount: number,
  timeSlot: string,
  occupied: Record<string, Record<string, any>>,
  pricingMap: Record<string, HallPricing>,
  mode: "cheapest" | "best_value" = "best_value",
): HallRecommendation | null {
  const recs = getHallRecommendations(guestCount, timeSlot, occupied, pricingMap);
  if (recs.length === 0) return null;

  if (mode === "cheapest") {
    return recs[0]; // already sorted by price
  }

  // best_value: closest capacity, then lowest price per guest
  const byValue = [...recs].sort(
    (a, b) => a.wastedCapacity - b.wastedCapacity || a.pricePerGuest - b.pricePerGuest,
  );
  return byValue[0];
}

/**
 * Capacity-only fallback when no pricing data is available.
 * Used during initial load before pricing arrives from the API.
 */
export function getCapacityOnlyRecommendation(
  guestCount: number,
  timeSlot: string,
  occupied: Record<string, Record<string, any>>,
): typeof HALL_OPTIONS[number] | null {
  if (guestCount <= 0) return null;

  const sorted = [...HALL_OPTIONS].sort((a, b) => a.capacity - b.capacity);
  for (const opt of sorted) {
    if (opt.capacity >= guestCount && isOptionAvailable(opt.key, timeSlot, occupied)) {
      return opt;
    }
  }
  return null;
}
